import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ApplicationHttpClient } from 'app/application-http-client';
import { DealerInfo } from 'app/modules/inventory/shared/dealerInfo';
import { camelCase, mapKeys } from 'lodash';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isNullOrUndefined } from 'is-what';
import { XRMjwtStoreService } from './xrmjwt-store.service';
import { Mfasettings } from '../models/mfa-settings.model';
import { MfaSetupSettings } from '../models/mfa-setup-settings.model';

export class LoginResult {
  public IsSuccess: boolean;
  public Message: string;
}

@Injectable()
export class LoginService {
  token_name = 'currentuser';
  public jwtHelper = new JwtHelperService();

  apiCheckPermission(): Observable<string> {
    return this.http.get('/jwt/jwt/GetPermission');
  }

  isLoggedIn(): boolean {
    return this.getDecodedJwtToken() ? true : false;
  }

  xstringLogin(xstring): Observable<LoginResult> {
    const params = ApplicationHttpClient.toHttpParams({
      xstring: xstring,
    });
    const curUser = localStorage.getItem('currentuser');
    if (curUser) {
      this.logout();
    }
    return this.http
      .get('/jwt/jwt/GetFromXstring', {
        params: params,
        errorNotification: true,
      })
      .pipe(
        map((response: any) => {
          const token = response && response.access_token;
          const result = new LoginResult();

          if (token) {
            result.IsSuccess = true;
            result.Message = null;
            localStorage.setItem(
              'currentuser',
              JSON.stringify({
                username: '',
                jwt_token: response.access_token,
                xString: xstring,
              })
            );
            return result;
          } else {
            result.IsSuccess = false;
            result.Message = response.message;
            return result;
          }
        }),
        catchError(function (e) {
          return Observable.throw('error returned! ' + e);
        })
      );
  }

  login(name, password, dealerId): Observable<LoginResult> {
    const headers = new HttpHeaders({ 'content-type': 'application/json' });
    return this.http
      .post(
        '/jwt/jwt/GetFromBody',
        JSON.stringify({
          UserName: name,
          Password: password,
          DealerID: dealerId,
        }),
        {
          headers: headers,
          rethrowErrors: true,
        }
      )
      .pipe(
        map((response: any) => {
          const token = response && response.access_token;
          const result = new LoginResult();

          if (token) {
            result.IsSuccess = true;
            result.Message = null;
            localStorage.setItem(
              'currentuser',
              JSON.stringify({
                username: name,
                jwt_token: response.access_token,
              })
            );

            // WPG 031418: localStorage data get lost issue
            this.xrmjwtSvc.setToken(
              JSON.stringify({
                username: name,
                jwt_token: response.access_token,
              })
            );
            return result;
          } else {
            result.IsSuccess = false;
            result.Message = response.message;
            return result;
          }
        })
      );
  }

  logout(): void {
    localStorage.removeItem(this.token_name);
    localStorage.removeItem('currentuser');
    // WPG 031418
    this.xrmjwtSvc.setToken('');
  }

  // seems no need for this. Use getDealerInfo( ) instead.
  readJwtPayload(claim_type: string) {
    const data = localStorage.getItem(this.token_name);
    if (!data) {
      return null;
    }

    const jwt = JSON.parse(data).jwt_token;
    if (!jwt) {
      return null;
    }

    const payload = this.jwtHelper.decodeToken(jwt);
    let res = null;
    for (const cname in payload) {
      if (cname.toUpperCase() === claim_type.toUpperCase()) {
        res = payload[cname];
      }
    }

    return res;
  }

  getDealerInfo(): DealerInfo {
    const token = this.getDecodedJwtToken();
    if (isNullOrUndefined(token)) {
      return null;
    }
    return Object.assign(
      {},
      mapKeys(token, (v, k) => camelCase(k))
    );
  }

  // return the original jwt token, regardless if it expires or not.
  getRawJwtToken() {
    const data = localStorage.getItem(this.token_name);
    if (!data) {
      return null;
    }

    const jsdata = JSON.parse(data);
    if (!jsdata) {
      return null;
    }
    const rawjwt = jsdata.jwt_token;
    if (!rawjwt) {
      return null;
    }

    return rawjwt;
  }

  // if jwt token exists AND has not expired, return decoded token;
  //  else return null
  getDecodedJwtToken() {
    const rawjwt = this.getRawJwtToken();

    if (!rawjwt) {
      return null;
    }

    if (this.jwtHelper.isTokenExpired(rawjwt)) {
      return null;
    }

    return this.jwtHelper.decodeToken(rawjwt);
  }

  getBaseRedirectQueryString(): string {
    const dealer = this.getDecodedJwtToken();
    // tslint:disable-next-line:max-line-length
    return `?program=xrm&enc=0&xdb=${dealer['DBName']}&xDealerID=${dealer['DealershipId']}&xUsername=${dealer['EmpId']}&xString=${dealer['xString']}&OUTSIDEXRM=Y`;
  }

  /**
   * get mfa settings
   * @returns mfa settings
   */
  getMfaSettings(): Observable<Mfasettings> {
    return this.http.get('/jwt/jwt/GetMfaSettings');
  }

  /**
   * get mfa setup setting
   * @returns mfa setup key and qr code
   */
  getMfaSetupSettings(): Observable<MfaSetupSettings> {
    return this.http.get('/jwt/jwt/GetMfaSetupSettings');
  }

  /**
   * enable mfa
   * @param securityPin 
   * @returns response result, true if setup otherwise false
   */
  enableMfa(securityPin: string): Observable<any> {
    return this.http.post('/jwt/jwt/EnableMfa', {
      securityPin: securityPin
    });
  }

  /**
   * mfa authorize
   * @param securityPin 
   * @returns response result, true if authorized otherwise false
   */
  mfaAuthorize(securityPin: string): Observable<any> {
    return this.http.post('/jwt/jwt/MfaAuthorize', {
      securityPin: securityPin
    });
  }

  constructor(
    private http: ApplicationHttpClient,
    private xrmjwtSvc: XRMjwtStoreService
  ) { }
}
